import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [selectedDateTime, setSelectedDateTime] = useState(null);

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('cartItems');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (err) {
        console.error('Error loading cart:', err);
      }
    }

    // Load selected date/time from localStorage
    const savedDateTime = localStorage.getItem('selectedDateTime');
    if (savedDateTime) {
      try {
        setSelectedDateTime(JSON.parse(savedDateTime));
      } catch (err) {
        console.error('Error loading date/time:', err);
      }
    }
  }, []);

  useEffect(() => {
    // Save cart to localStorage whenever it changes
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    // Save selected date/time to localStorage whenever it changes
    if (selectedDateTime) {
      localStorage.setItem('selectedDateTime', JSON.stringify(selectedDateTime));
    } else {
      localStorage.removeItem('selectedDateTime');
    }
  }, [selectedDateTime]);

  const addToCart = (service, category) => {
    const cartItem = {
      id: `${category}-${service.id}`,
      serviceId: service.id,
      name: service.name,
      description: service.description,
      pricePerHour: service.pricePerHour,
      category: category,
      quantity: 1
    };

    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === cartItem.id);
      if (existingItem) {
        // If item already exists, increase quantity
        return prevItems.map(item =>
          item.id === cartItem.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Add new item to cart
        return [...prevItems, cartItem];
      }
    });
  };

  const removeFromCart = (itemId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setSelectedDateTime(null);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => {
      return total + (item.pricePerHour * item.quantity);
    }, 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const setSelectedDateTimeSlot = (dateTime) => {
    setSelectedDateTime(dateTime);
  };

  const clearSelectedDateTime = () => {
    setSelectedDateTime(null);
  };

  const value = {
    cartItems,
    selectedDateTime,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalPrice,
    getTotalItems,
    setSelectedDateTimeSlot,
    clearSelectedDateTime
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

